import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ReactiveFormsModule, FormGroup, FormControl } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { Store } from '@ngrx/store';
import { InputModule } from '@sharedModule/components/input/input.module';
import { sortDirection } from '@sharedModule/constants/data-grid.constants';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { HasAccessDirective } from '@sharedModule/directives/has-access.directive';
import { MaterialModule } from '@sharedModule/material/material.module';
import { ConfirmationCommonDialogData } from '@sharedModule/models/confirmation-dialog.model';
import { ConfirmationDialogService } from '@sharedModule/services/confirmation-dialog/confirmation-dialog.service';
import { ExcelExportService } from '@sharedModule/services/excel-export/excel-export.service';
import { getActiveSort } from '@sharedModule/utils/sort.utils';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { PartnerService } from '../../services/partner.service';
import {
  updatePartnerListFilter,
  deletePartner,
  resetPartnerList,
} from '../../store/partner-list.actions';
import { selectPartnerList } from '../../store/partner-list.selector';
import { PartnerListInterface } from '../../types/partner-list';
import {
  defaultPartnerListFilter,
  PartnerListFilter,
} from '../../types/partner-list-filter';

@Component({
  selector: 'app-partner-list',
  standalone: true,
  imports: [
    InputModule,
    CommonModule,
    RouterModule,
    MaterialModule,
    HasAccessDirective,
    ReactiveFormsModule,
  ],
  templateUrl: './partner-list.component.html',
  styleUrl: './partner-list.component.scss',
})
export class PartnerListComponent implements OnInit, OnDestroy {
  public readonly attrConstant = ATTRIBUTES_CONSTANT.partners;
  filterForm!: FormGroup;
  displayedColumns: string[] = [
    'partner_id',
    'name',
    'email',
    'mode',
    'created_at',
    'is_active',
    'action',
  ];
  partnerList?: PartnerListInterface[];
  dataSource!: MatTableDataSource<PartnerListInterface>;
  activeSort?: Sort;
  fallBackSort = {
    active: 'created_at',
    direction: sortDirection.desc,
  };
  page = 0;
  total = 0;
  limit = 10;
  subject = new Subject<{
    search: string;
  }>();
  #destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private partnerService: PartnerService,
    private excelExportService: ExcelExportService,
    private messageHandlerService: MessageHandlerService,
    private confirmationDialogService: ConfirmationDialogService
  ) {}

  ngOnInit(): void {
    this.filterFormInit();
    this.loadData();
    this.dispatchFilter();
    const debounceTimeDuration = 1000;
    this.subject
      .pipe(debounceTime(debounceTimeDuration), takeUntil(this.#destroy$))
      .subscribe(res => {
        this.dispatchFilter({ count: 10, page: 0, ...res });
      });
  }

  filterFormInit(): void {
    this.filterForm = new FormGroup({
      [this.attrConstant.search.key]: new FormControl(''),
    });
  }

  loadData(): void {
    this.store
      .select(selectPartnerList)
      .pipe(takeUntil(this.#destroy$))
      .subscribe(data => {
        if (data) {
          this.partnerList = data.rawData;
          this.dataSource = new MatTableDataSource(data.rawData);
          this.page = data.page;
          this.total = data.totalRecords;
          this.limit = data.count;
        }
      });
  }

  applyFilter(): void {
    this.subject.next(this.filterForm.value);
  }

  clearFilters(): void {
    this.filterForm.reset();
    this.dispatchFilter({ ...defaultPartnerListFilter });
  }

  reload(): void {
    this.dispatchFilter();
  }

  onPageChange(pageEvent: PageEvent): void {
    this.dispatchFilter({
      count: pageEvent.pageSize,
      page: pageEvent.pageIndex,
    });
  }

  onSortChange(sortEvent: Sort): void {
    this.activeSort = getActiveSort(sortEvent, this.fallBackSort);
    this.dispatchFilter({
      sort_key: this.activeSort.active,
      sort_by: this.activeSort.direction,
    });
  }

  dispatchFilter(partnerListFilter?: PartnerListFilter): void {
    this.store.dispatch(
      updatePartnerListFilter({ filter: partnerListFilter ?? {} })
    );
  }

  export(): void {
    this.excelExportService.exportToExcel(this.partnerList!, 'partners');
  }

  async onToggle(
    event: MatSlideToggleChange,
    data: PartnerListInterface
  ): Promise<void> {
    const alertCommonDialogData: ConfirmationCommonDialogData = {
      title: '',
      message: `Are you sure you're going to mark it as ${data.is_active ? 'in-active' : 'active'}?`,
      submitButtonText: 'Confirm',
    };
    const isConfirmed =
      await this.confirmationDialogService.openConfirmationDialog(
        alertCommonDialogData
      );
    if (isConfirmed) this.changeActiveStatus(event, data);
    else event.source.checked = !event.source.checked;
  }

  changeActiveStatus(
    event: MatSlideToggleChange,
    data: PartnerListInterface
  ): void {
    const payload = {
      partner_id: data.partner_id,
      is_active: event.checked,
    };
    this.partnerService
      .updatePartnerStatus(payload)
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) this.messageHandlerService.show(x.message);
          else event.source.checked = !event.source.checked;
        },
      });
  }

  async onDeletePartner(data: PartnerListInterface): Promise<void> {
    const alertCommonDialogData: ConfirmationCommonDialogData = {
      title: '',
      message: `Are you sure you're going to delete this partner?`,
      submitButtonText: 'Confirm',
    };
    const isConfirmed =
      await this.confirmationDialogService.openConfirmationDialog(
        alertCommonDialogData
      );
    if (isConfirmed) {
      this.partnerService
        .deletePartner({ partner_id: data.partner_id })
        .pipe(takeUntil(this.#destroy$))
        .subscribe({
          next: x => {
            if (x.success) {
              this.messageHandlerService.show(x.message);
              this.store.dispatch(
                deletePartner({
                  partner_id: data.partner_id,
                })
              );
            }
          },
        });
    }
  }

  ngOnDestroy(): void {
    this.store.dispatch(resetPartnerList());
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
